import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  CustomerAccountsStorefrontDisplayFlatTeamSettingGroupById,
  type CustomerAccountsStorefrontDisplayFlatTeamSettingGroupId,
} from "./groups";

type CustomerAccountsStorefrontDisplaySettingInput =
  SettingRegistryMetadataInput;

function setting(
  metadata: CustomerAccountsStorefrontDisplaySettingInput,
): CustomerAccountsStorefrontDisplaySettingInput {
  return metadata;
}

function customerAccountsStorefrontDisplayGroup<
  const Group extends CustomerAccountsStorefrontDisplayFlatTeamSettingGroupId,
  const T extends Record<string, CustomerAccountsStorefrontDisplaySettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const CustomerAccountsStorefrontDisplayRegistry = {
  ...customerAccountsStorefrontDisplayGroup(
    "customer_accounts_storefront_display.save_for_later",
    {
      "customerAccounts/saveForLaterPanel/enabled": setting({
        label: "Enable save for later pop-up",
        description:
          "Show the save-for-later pop-up that asks shoppers to save viewed items for later.",
        synonyms: [
          "save for later",
          "save for later pop-up",
          "save for later panel",
          "enable save for later",
          "enable save for later pop-up",
        ],
      }),
      "customerAccounts/saveForLaterPanel/title": setting({
        label: "Save for later title",
        description: "Title shown on the save-for-later pop-up.",
        synonyms: [
          "save for later title",
          "save for later heading",
          "panel title",
        ],
      }),
      "customerAccounts/saveForLaterPanel/message": setting({
        label: "Save for later message",
        description: "Message shown on the save-for-later pop-up.",
        synonyms: [
          "save for later message",
          "save for later body",
          "panel message",
        ],
      }),
      "customerAccounts/saveForLaterPanel/numViewedProductsUntilPanelOpen":
        setting({
          label: "Number of viewed products until panel opens",
          description:
            "Number of items a shopper must view before the save-for-later pop-up appears.",
          synonyms: [
            "viewed products until panel opens",
            "products viewed threshold",
            "save for later trigger count",
          ],
        }),
      "customerAccounts/saveForLaterPanel/anchorLocation": setting({
        label: "Save for later anchor location",
        description:
          "Screen anchor location where the save-for-later slide-in panel appears.",
        synonyms: [
          "anchor location",
          "save for later position",
          "panel position",
        ],
      }),
    },
  ),
  ...customerAccountsStorefrontDisplayGroup(
    "customer_accounts_storefront_display.native_accounts",
    {
      "customerAccounts/nativeAccounts/overrideAccountLinks": setting({
        label: "Override account links",
        description:
          "Open storefront account links in the accounts view of the customer widget.",
        synonyms: [
          "override account links",
          "account links",
          "native account links",
        ],
      }),
      "customerAccounts/nativeAccounts/redirectFromAccountsPages": setting({
        label: "Redirect from native accounts pages",
        description:
          "Replace native storefront accounts pages with the customer widget.",
        synonyms: [
          "redirect from accounts pages",
          "redirect native accounts",
          "native accounts redirect",
        ],
      }),
    },
  ),
};

type CustomerAccountsStorefrontDisplayFlatTeamSettingKey =
  keyof typeof CustomerAccountsStorefrontDisplayRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const CustomerAccountsStorefrontDisplayFlatTeamSettingKeys = Object.keys(
  CustomerAccountsStorefrontDisplayRegistry,
) as CustomerAccountsStorefrontDisplayFlatTeamSettingKey[];

export const CustomerAccountsStorefrontDisplayFlatTeamSettingCatalogDefinitions =
  CustomerAccountsStorefrontDisplayFlatTeamSettingKeys.map((key) => {
    const definition = CustomerAccountsStorefrontDisplayRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group:
          CustomerAccountsStorefrontDisplayFlatTeamSettingGroupById[
            definition.group
          ],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    CustomerAccountsStorefrontDisplayFlatTeamSettingKey,
    CustomerAccountsStorefrontDisplayFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<CustomerAccountsStorefrontDisplayFlatTeamSettingKey>
  >[];

export const CustomerAccountsStorefrontDisplayFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    CustomerAccountsStorefrontDisplayFlatTeamSettingCatalogDefinitions.map(
      (definition) => [definition.id, definition.metadata],
    ),
  ) as Record<
    CustomerAccountsStorefrontDisplayFlatTeamSettingKey,
    SettingMetadata
  >;
